import React, { useState, useEffect, useRef } from "react";
import SideBar from "../components/SideBar";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../styles/EditCustomerProfile.css";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import Select from "react-select";
import { retailerService } from "../services/RetailerServices";
import { toast } from "react-toastify";
import { API_URL } from "../constant";
import { clampAdminPhoneString, phoneErrorSuperAdmin } from "../utils/phone";

function EditRetailerProfile() {
  const [step, setStep] = useState(1);
  const location = useLocation();
  const retail = location.state?.retail || {};
  const navigate = useNavigate();
  const lastSelectedMobileCountryRef = useRef(null);

  const getPhoneIso2 = (phoneCountry) => {
    if (!phoneCountry) return undefined;
    return phoneCountry.countryCode || phoneCountry.iso2 || undefined;
  };
  const [loading, setLoading] = useState(false);
  const [validatingFields, setValidatingFields] = useState(false);

  // State for retail types
  const [retailTypesData, setRetailTypesData] = useState({});
  const [loadingRetailTypes, setLoadingRetailTypes] = useState(false);

  // Owner Details - Only personal information
  const [firstName, setFirstName] = useState(retail.first_name || "");
  const [lastName, setLastName] = useState(retail.last_name || "");

  // Business Details
  const [email, setEmail] = useState(retail.email || "");
  const [mobile, setMobile] = useState(retail.phone_number || "");
  const [ownerPhoto, setOwnerPhoto] = useState(retail.photo_url || "");
  const [photoFile, setPhotoFile] = useState(null);
  const [retailName, setRetailName] = useState(
    retail.shop_details?.retail_name || "",
  );

  // Retail Type & Sub Type (as strings)
  const [retailType, setRetailType] = useState(
    retail.shop_details?.retail_type || "",
  );
  const [retailSubType, setRetailSubType] = useState(
    retail.shop_details?.retail_sub_type || "",
  );
  const [retailSubTypeOptions, setRetailSubTypeOptions] = useState([]);

  const [shopAddress, setShopAddress] = useState(
    retail.shop_details?.retail_address || "",
  );
  const [website, setWebsite] = useState(retail.shop_details?.website || "");
  const [signedContract, setSignedContract] = useState(
    retail.shop_details?.signed_contract || false,
  );
  const [shopStatus, setShopStatus] = useState(
    retail.shop_details?.status || "active",
  );

  // Business Address
  const [line1, setLine1] = useState(retail.address_line1 || "");
  const [line2, setLine2] = useState(retail.address_line2 || "");
  const [line3, setLine3] = useState(retail.address_line3 || "");
  const [selectedCity, setSelectedCity] = useState(retail.city || "");
  const [postalCode, setPostalCode] = useState(retail.postcode || "");
  const [selectedCounty, setSelectedCounty] = useState(retail.region || "");
  const [selectedCountry, setSelectedCountry] = useState(retail.country || "");

  // Status
  const [status, setStatus] = useState(
    retail.current_status
      ? { value: retail.current_status, label: retail.current_status }
      : null,
  );
  const [note, setNote] = useState(retail.status_note || "");

  // Error states
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [line1Error, setLine1Error] = useState("");
  const [cityError, setCityError] = useState("");
  const [postalCodeError, setPostalCodeError] = useState("");
  const [retailNameError, setRetailNameError] = useState("");
  const [retailTypeError, setRetailTypeError] = useState("");
  const [retailSubTypeError, setRetailSubTypeError] = useState("");
  const [websiteError, setWebsiteError] = useState("");

  // Region options
  const regionOptions = {
    // Africa
    Afghanistan: ["Asia"],
    Albania: ["Europe"],
    Algeria: ["Africa"],
    Andorra: ["Europe"],
    Angola: ["Africa"],
    Argentina: ["South America"],
    Armenia: ["Asia"],
    Australia: ["Oceania"],
    Austria: ["Europe"],
    Azerbaijan: ["Asia"],
    Bahamas: ["North America"],
    Bahrain: ["Asia"],
    Bangladesh: ["Asia"],
    Barbados: ["North America"],
    Belarus: ["Europe"],
    Belgium: ["Europe"],
    Belize: ["North America"],
    Benin: ["Africa"],
    Bhutan: ["Asia"],
    Bolivia: ["South America"],
    "Bosnia and Herzegovina": ["Europe"],
    Botswana: ["Africa"],
    Brazil: ["South America"],
    Brunei: ["Asia"],
    Bulgaria: ["Europe"],
    "Burkina Faso": ["Africa"],
    Burundi: ["Africa"],
    Cambodia: ["Asia"],
    Cameroon: ["Africa"],
    Canada: ["Alberta", "British Columbia", "Manitoba", "Ontario", "Quebec"],
    "Cape Verde": ["Africa"],
    "Central African Republic": ["Africa"],
    Chad: ["Africa"],
    Chile: ["South America"],
    China: ["Asia"],
    Colombia: ["South America"],
    Comoros: ["Africa"],
    Congo: ["Africa"],
    "Costa Rica": ["North America"],
    Croatia: ["Europe"],
    Cuba: ["North America"],
    Cyprus: ["Europe"],
    "Czech Republic": ["Europe"],
    Denmark: ["Europe"],
    Djibouti: ["Africa"],
    "Dominican Republic": ["North America"],
    Ecuador: ["South America"],
    Egypt: ["Africa"],
    "El Salvador": ["North America"],
    Estonia: ["Europe"],
    Ethiopia: ["Africa"],
    Finland: ["Europe"],
    France: ["Europe"],
    Germany: ["Europe"],
    Ghana: ["Africa"],
    Greece: ["Europe"],
    Guatemala: ["North America"],
    Haiti: ["North America"],
    Honduras: ["North America"],
    Hungary: ["Europe"],
    Iceland: ["Europe"],
    India: [
      "Andhra Pradesh",
      "Delhi",
      "Karnataka",
      "Maharashtra",
      "Tamil Nadu",
      "West Bengal",
    ],
    Indonesia: ["Asia"],
    Iran: ["Tehran", "Isfahan", "Fars", "Mazandaran", "Khorasan Razavi"],
    Iraq: ["Asia"],
    Ireland: ["Europe"],
    Israel: ["Asia"],
    Italy: ["Europe"],
    Jamaica: ["North America"],
    Japan: ["Asia"],
    Jordan: ["Asia"],
    Kazakhstan: ["Asia"],
    Kenya: ["Africa"],
    Kuwait: ["Asia"],
    Kyrgyzstan: ["Asia"],
    Laos: ["Asia"],
    Latvia: ["Europe"],
    Lebanon: ["Asia"],
    Lesotho: ["Africa"],
    Liberia: ["Africa"],
    Libya: ["Africa"],
    Lithuania: ["Europe"],
    Luxembourg: ["Europe"],
    Madagascar: ["Africa"],
    Malawi: ["Africa"],
    Malaysia: ["Asia"],
    Maldives: ["Asia"],
    Mali: ["Africa"],
    Malta: ["Europe"],
    Mexico: ["North America"],
    Moldova: ["Europe"],
    Mongolia: ["Asia"],
    Morocco: ["Africa"],
    Mozambique: ["Africa"],
    Myanmar: ["Asia"],
    Namibia: ["Africa"],
    Nepal: ["Asia"],
    Netherlands: ["Europe"],
    "New Zealand": ["Oceania"],
    Nicaragua: ["North America"],
    Niger: ["Africa"],
    Nigeria: ["Africa"],
    "North Korea": ["Asia"],
    Norway: ["Europe"],
    Oman: ["Asia"],
    Pakistan: ["Asia"],
    Panama: ["North America"],
    Paraguay: ["South America"],
    Peru: ["South America"],
    Philippines: ["Asia"],
    Poland: ["Europe"],
    Portugal: ["Europe"],
    Qatar: ["Asia"],
    Romania: ["Europe"],
    Russia: ["Europe"],
    Rwanda: ["Africa"],
    "Saudi Arabia": ["Asia"],
    Senegal: ["Africa"],
    Serbia: ["Europe"],
    Singapore: ["Asia"],
    Slovakia: ["Europe"],
    Slovenia: ["Europe"],
    Somalia: ["Africa"],
    "South Africa": ["Africa"],
    "South Korea": ["Asia"],
    Spain: ["Europe"],
    "Sri Lanka": [
      "Central Province",
      "Eastern Province",
      "Northern Province",
      "Southern Province",
      "Western Province",
      "North Central Province",
      "North Western Province",
      "Sabaragamuwa Province",
      "Uva Province",
    ],
    Sudan: ["Africa"],
    Sweden: ["Europe"],
    Switzerland: ["Europe"],
    Syria: ["Asia"],
    Taiwan: ["Asia"],
    Tanzania: ["Africa"],
    Thailand: ["Asia"],
    Tunisia: ["Africa"],
    Turkey: ["Asia"],
    Uganda: ["Africa"],
    Ukraine: ["Europe"],
    "United Arab Emirates": ["Asia"],
    "United Kingdom": [
      "England - Bedfordshire",
      "England - Berkshire",
      "England - Bristol",
      "England - Buckinghamshire",
      "England - Cambridgeshire",
      "England - Cheshire",
      "England - City of London",
      "England - Cornwall",
      "England - Cumbria",
      "England - Derbyshire",
      "England - Devon",
      "England - Dorset",
      "England - Durham",
      "England - East Riding of Yorkshire",
      "England - East Sussex",
      "England - Essex",
      "England - Gloucestershire",
      "England - Greater London",
      "England - Greater Manchester",
      "England - Hampshire",
      "England - Herefordshire",
      "England - Hertfordshire",
      "England - Isle of Wight",
      "England - Kent",
      "England - Lancashire",
      "England - Leicestershire",
      "England - Lincolnshire",
      "England - Merseyside",
      "England - Norfolk",
      "England - North Yorkshire",
      "England - Northamptonshire",
      "England - Northumberland",
      "England - Nottinghamshire",
      "England - Oxfordshire",
      "England - Rutland",
      "England - Shropshire",
      "England - Somerset",
      "England - South Yorkshire",
      "England - Staffordshire",
      "England - Suffolk",
      "England - Surrey",
      "England - Sussex",
      "England - Tyne & Wear",
      "England - Warwickshire",
      "England - West Midlands",
      "England - West Sussex",
      "England - West Yorkshire",
      "England - Wiltshire",
      "England - Worcestershire",
      "Northern Ireland - Antrim",
      "Northern Ireland - Armagh",
      "Northern Ireland - Down",
      "Northern Ireland - Fermanagh",
      "Northern Ireland - Londonderry",
      "Northern Ireland - Tyrone",
      "Scotland - City of Aberdeen",
      "Scotland - Aberdeenshire",
      "Scotland - Angus",
      "Scotland - Argyll",
      "Scotland - Ayrshire",
      "Scotland - Banffshire",
      "Scotland - Berwickshire",
      "Scotland - Bute",
      "Scotland - Caithness",
      "Scotland - Clackmannanshire",
      "Scotland - Dumfriesshire",
      "Scotland - Dunbartonshire",
      "Scotland - City of Dundee",
      "Scotland - East Lothian",
      "Scotland - City of Edinburgh",
      "Scotland - Fife",
      "Scotland - City of Glasgow",
      "Scotland - Inverness-shire",
      "Scotland - Kincardineshire",
      "Scotland - Kinross-shire",
      "Scotland - Kirkcudbrightshire",
      "Scotland - Lanarkshire",
      "Scotland - Midlothian",
      "Scotland - Moray",
      "Scotland - Nairnshire",
      "Scotland - Orkney",
      "Scotland - Peeblesshire",
      "Scotland - Perthshire",
      "Scotland - Renfrewshire",
      "Scotland - Ross and Cromarty",
      "Scotland - Roxburghshire",
      "Scotland - Selkirkshire",
      "Scotland - Shetland",
      "Scotland - Stirlingshire",
      "Scotland - Sutherland",
      "Scotland - West Lothian",
      "Scotland - Wigtownshire",
      "Wales - Blaenau Gwent",
      "Wales - Bridgend",
      "Wales - Caerphilly",
      "Wales - Carmarthenshire",
      "Wales - Ceredigion",
      "Wales - Swansea",
      "Wales - Cardiff",
      "Wales - Conwy",
      "Wales - Denbighshire",
      "Wales - Flintshire",
      "Wales - Gwynedd",
      "Wales - Isle of Anglesey",
      "Wales - Merthyr Tydfil",
      "Wales - Monmouthshire",
      "Wales - Neath Port Talbot",
      "Wales - Newport",
      "Wales - Pembrokeshire",
      "Wales - Powys",
      "Wales - Rhondda Cynon Taf",
      "Wales - Torfaen",
      "Wales - Vale of Glamorgan",
      "Wales - Wrexham",
    ],
    "United States": ["North America"],
    Uruguay: ["South America"],
    Uzbekistan: ["Asia"],
    Venezuela: ["South America"],
    Vietnam: ["Asia"],
    Yemen: ["Asia"],
    Zambia: ["Africa"],
    Zimbabwe: ["Africa"],
  };

  const statusOptions = [
    { value: "approved", label: "Approved" },
    { value: "awaiting", label: "Awaiting" },
    { value: "suspended", label: "Suspended" },
    { value: "rejected", label: "Rejected" },
  ];

  const shopStatusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  // Retail type options (will be populated from API)
  const [retailTypeOptions, setRetailTypeOptions] = useState([]);

  // Get current country option for select
  const currentCountryOption = selectedCountry
    ? { value: selectedCountry, label: selectedCountry }
    : null;
  // Get current region option for select
  const currentRegionOption = selectedCounty
    ? { value: selectedCounty, label: selectedCounty }
    : null;
  // Get current retail type option for select
  const currentRetailTypeOption = retailType
    ? { value: retailType, label: retailType }
    : null;
  // Get current retail sub-type option for select
  const currentRetailSubTypeOption = retailSubType
    ? { value: retailSubType, label: retailSubType }
    : null;
  // Get current shop status option for select
  const currentShopStatusOption = shopStatusOptions.find(
    (opt) => opt.value === shopStatus,
  );

  // Fetch retail types on component mount
  useEffect(() => {
    fetchRetailTypes();
  }, []);

  // Update sub-type options when retail type changes or data loads
  useEffect(() => {
    if (retailType && retailTypesData[retailType]) {
      const subTypeOptions = retailTypesData[retailType].map((subType) => ({
        value: subType,
        label: subType,
      }));
      setRetailSubTypeOptions(subTypeOptions);

      // If we have an existing retail sub-type, make sure it's in the options
      if (
        retailSubType &&
        !subTypeOptions.find((opt) => opt.value === retailSubType)
      ) {
        // If current sub-type is not in options, reset it
        setRetailSubType("");
      }
    } else {
      setRetailSubTypeOptions([]);
    }
  }, [retailType, retailTypesData, retailSubType]);

  // Fetch retail types from backend
  const fetchRetailTypes = async () => {
    setLoadingRetailTypes(true);
    try {
      const response = await fetch(`${API_URL}/retail-types/registration-data`);
      const data = await response.json();

      if (data.success) {
        // Store the grouped data
        setRetailTypesData(data.data);

        // Create options for retail type dropdown
        const typeOptions = Object.keys(data.data).map((type) => ({
          value: type,
          label: type,
        }));
        setRetailTypeOptions(typeOptions);
      } else {
        toast.error("Failed to load retail types");
        // Fallback to default options
        setRetailTypesData(fallbackRetailTypesData);
        setRetailTypeOptions(fallbackRetailTypeOptions);
      }
    } catch (error) {
      console.error("Error fetching retail types:", error);
      toast.error("Failed to load retail types");
      // Fallback to default options
      setRetailTypesData(fallbackRetailTypesData);
      setRetailTypeOptions(fallbackRetailTypeOptions);
    } finally {
      setLoadingRetailTypes(false);
    }
  };

  // Fallback data
  const fallbackRetailTypesData = {
    Clothing: ["Men's Wear", "Women's Wear", "Children's Wear"],
    Electronics: ["Mobile Phones", "Computers", "Home Appliances"],
    Grocery: ["Supermarket", "Convenience Store", "Specialty Food"],
    Furniture: ["Home Furniture", "Office Furniture", "Garden Furniture"],
    Other: ["Other"],
  };

  const fallbackRetailTypeOptions = Object.keys(fallbackRetailTypesData).map(
    (type) => ({
      value: type,
      label: type,
    }),
  );

  // Check if email exists
  const checkEmailExists = async (email) => {
    try {
      // Skip check if email hasn't changed
      if (email === retail.email) {
        return false;
      }

      const response = await fetch(
        `${API_URL}/check-email?email=${encodeURIComponent(email)}`,
      );
      const data = await response.json();
      return data.exists || false;
    } catch (error) {
      console.error("Error checking email:", error);
      return false;
    }
  };

  // Function to normalize website URL
  const normalizeWebsiteUrl = (url) => {
    if (!url || url.trim() === "") return "";

    let normalizedUrl = url.trim();

    // Remove any leading/trailing whitespace
    normalizedUrl = normalizedUrl.trim();

    // If it starts with www., add https://
    if (normalizedUrl.startsWith("www.")) {
      normalizedUrl = "https://" + normalizedUrl;
    }
    // If it doesn't start with http:// or https://, add https://
    else if (
      !normalizedUrl.startsWith("http://") &&
      !normalizedUrl.startsWith("https://")
    ) {
      // Check if it looks like a domain (contains a dot)
      if (normalizedUrl.includes(".") && !normalizedUrl.includes(" ")) {
        normalizedUrl = "https://" + normalizedUrl;
      }
    }

    return normalizedUrl;
  };

  // Function to validate website URL
  const validateWebsiteUrl = (url) => {
    if (!url || url.trim() === "") return { isValid: true, message: "" };

    const urlStr = url.trim();

    // Basic domain pattern validation - accepts all formats
    const domainPattern =
      /^(https?:\/\/)?(www\.)?[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?(\/.*)?$/;

    if (!domainPattern.test(urlStr)) {
      return {
        isValid: false,
        message:
          "Please enter a valid website URL (e.g., example.com, www.example.com, https://example.com)",
      };
    }

    return { isValid: true, message: "" };
  };

  const nextStep = async () => {
    if (step === 1) {
      const isValid = await validateOwnerDetails();
      if (!isValid) return;
    }
    if (step === 2) {
      const isValid = await validateBusinessDetails();
      if (!isValid) return;
    }
    if (step === 3) {
      const isValid = validateBusinessAddress();
      if (!isValid) return;
    }
    setStep((prev) => Math.min(prev + 1, 4)); // Changed max to 4
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  // Function to handle direct step click - NO VALIDATION
  const handleStepClick = (stepNumber) => {
    // Don't allow clicking on current step
    if (stepNumber === step) return;

    // Direct navigation without validation
    setStep(stepNumber);
  };

  const steps = [
    { label: "Owner Details", sub: "Enter owner name" },
    { label: "Business Details", sub: "Enter business information" },
    { label: "Business Address", sub: "Enter business address" },
    { label: "Status", sub: "Update the status" },
    // Removed "Key Management" step
  ];

  const handleUpdate = async () => {
    if (!validateAllSteps()) {
      toast.error("Please fix validation errors before updating.");
      return;
    }

    setLoading(true);
    try {
      // Prepare form data for file upload
      const formData = new FormData();

      // Add owner personal data
      formData.append("first_name", firstName);
      formData.append("last_name", lastName);

      // Add business contact data
      formData.append("email", email);
      formData.append("phone_number", mobile);

      // Add business address
      formData.append("address_line1", line1);
      formData.append("address_line2", line2 || "");
      formData.append("address_line3", line3 || "");
      formData.append("city", selectedCity);
      formData.append("postcode", postalCode);

      // Add country data
      if (selectedCountry) {
        formData.append("country_name", selectedCountry);
        formData.append("region", selectedCounty);
      }

      // Add business details
      formData.append("retail_name", retailName);
      formData.append("retail_type", retailType); // Store as string
      formData.append("retail_sub_type", retailSubType); // Store as string
      formData.append("retail_address", shopAddress);

      // Normalize website URL before sending
      const normalizedWebsite = website ? normalizeWebsiteUrl(website) : "";
      if (normalizedWebsite) {
        formData.append("website", normalizedWebsite);
      }

      formData.append("signed_contract", signedContract ? "1" : "0");
      formData.append("shop_status", shopStatus);

      // Add photo if changed
      if (photoFile) {
        formData.append("photo", photoFile);
      }

      console.log("Updating retailer with ID:", retail.retail_business_id);

      // Call the API to update retailer
      const response = await retailerService.updateRetailer(
        retail.retail_business_id,
        formData,
      );

      // If status is changed, update it separately
      if (status && status.value !== retail.current_status) {
        await retailerService.updateRetailerStatus(retail.retail_business_id, {
          user_status: status.value,
          notes: note || `Status changed to ${status.value}`,
        });
      }

      toast.success("Retailer updated successfully!");

      // Prepare updated retail data for navigation
      const updatedRetail = {
        ...retail,
        first_name: firstName,
        last_name: lastName,
        email: email,
        phone_number: mobile,
        address_line1: line1,
        address_line2: line2,
        address_line3: line3,
        city: selectedCity,
        postcode: postalCode,
        country: selectedCountry,
        region: selectedCounty,
        current_status: status?.value || retail.current_status,
        status_note: note,
        shop_details: {
          ...retail.shop_details,
          retail_name: retailName,
          retail_type: retailType,
          retail_sub_type: retailSubType,
          retail_address: shopAddress,
          website: normalizedWebsite || website,
          signed_contract: signedContract,
          status: shopStatus,
        },
      };

      // Add photo URL if it was updated and returned in response
      if (response.data?.user?.photo_url) {
        updatedRetail.photo_url = response.data.user.photo_url;
      }

      console.log(
        "Navigating to retailer-shop-details with data:",
        updatedRetail,
      );

      // Navigate back to retailer details with updated data
      navigate("/retailer-shop-details", {
        state: {
          retail: updatedRetail,
        },
      });
    } catch (error) {
      console.error("Update failed:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update retailer";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateAllSteps = () => {
    const step1Valid = validateOwnerDetailsSync();
    const step2Valid = validateBusinessDetailsSync();
    const step3Valid = validateBusinessAddress();

    return step1Valid && step2Valid && step3Valid;
  };

  // Synchronous validation for step 1 (without API calls)
  const validateOwnerDetailsSync = () => {
    let valid = true;

    // First name validation
    if (!firstName.trim()) {
      setFirstNameError("First name is required.");
      valid = false;
    } else if (!/^[A-Za-z\s]+$/.test(firstName)) {
      setFirstNameError("First name must contain only letters.");
      valid = false;
    } else if (firstName.trim().length < 3) {
      setFirstNameError("First name must be at least 3 letters long.");
      valid = false;
    } else {
      setFirstNameError("");
    }

    // Last name validation
    if (!lastName.trim()) {
      setLastNameError("Last name is required.");
      valid = false;
    } else if (!/^[A-Za-z\s]+$/.test(lastName)) {
      setLastNameError("Last name must contain only letters.");
      valid = false;
    } else if (lastName.trim().length < 3) {
      setLastNameError("Last name must be at least 3 letters long.");
      valid = false;
    } else {
      setLastNameError("");
    }

    return valid;
  };

  // Async validation for step 1 (with API calls)
  const validateOwnerDetails = async () => {
    // First do basic validation
    if (!validateOwnerDetailsSync()) {
      return false;
    }

    return true;
  };

  // Synchronous validation for step 2 (without API calls)
  const validateBusinessDetailsSync = () => {
    let valid = true;
    const mobileIso2 = getPhoneIso2(lastSelectedMobileCountryRef.current);

    // Email validation
    if (!email.trim()) {
      setEmailError("Business email is required.");
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    } else {
      setEmailError("");
    }

    // Phone validation
    const mobileErr = phoneErrorSuperAdmin(mobile, mobileIso2);
    if (mobileErr) {
      setMobileError(mobileErr);
      valid = false;
    } else {
      setMobileError("");
    }

    // Business name validation
    if (!retailName.trim()) {
      setRetailNameError("Business name is required.");
      valid = false;
    } else {
      setRetailNameError("");
    }

    // Retail Type validation
    if (!retailType.trim()) {
      setRetailTypeError("Retail type is required.");
      valid = false;
    } else {
      setRetailTypeError("");
    }

    // Retail Sub Type validation
    if (!retailSubType.trim()) {
      setRetailSubTypeError("Retail sub-type is required.");
      valid = false;
    } else {
      setRetailSubTypeError("");
    }

    // Website validation (only if provided)
    if (website.trim()) {
      const validation = validateWebsiteUrl(website);
      if (!validation.isValid) {
        setWebsiteError(validation.message);
        valid = false;
      } else {
        setWebsiteError("");
      }
    } else {
      setWebsiteError("");
    }

    return valid;
  };

  // Async validation for step 2 (with API calls)
  const validateBusinessDetails = async () => {
    // First do basic validation
    if (!validateBusinessDetailsSync()) {
      return false;
    }

    // Check if email has changed
    const emailChanged = email !== retail.email;

    // If email hasn't changed, proceed
    if (!emailChanged) {
      return true;
    }

    setValidatingFields(true);

    try {
      // Check email existence if it's changed
      const emailExists = await checkEmailExists(email);

      let hasErrors = false;

      if (emailExists) {
        setEmailError(
          "This email is already registered. Please use a different email.",
        );
        hasErrors = true;
      }

      if (hasErrors) {
        toast.error("Email already exists. Please use different credentials.");
        return false;
      }

      return true;
    } catch (error) {
      toast.error("Error checking availability. Please try again.");
      return false;
    } finally {
      setValidatingFields(false);
    }
  };

  const validateBusinessAddress = () => {
    let valid = true;

    if (!line1.trim()) {
      setLine1Error("Address line 1 is required.");
      valid = false;
    } else {
      setLine1Error("");
    }

    if (!selectedCity.trim()) {
      setCityError("City is required.");
      valid = false;
    } else {
      setCityError("");
    }

    // Postal code validation
    if (!postalCode.trim()) {
      setPostalCodeError("Postal code is required.");
      valid = false;
    } else if (!/^[A-Za-z0-9\s\-]+$/.test(postalCode)) {
      setPostalCodeError("Please enter a valid postal code.");
      valid = false;
    } else {
      setPostalCodeError("");
    }

    return valid;
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type and size
      const validTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/gif",
        "image/avif",
        "image/webp",
      ];
      if (!validTypes.includes(file.type)) {
        toast.error(
          "Please select a valid image file (JPEG, PNG, JPG, GIF, AVIF, WEBP)",
        );
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        // 2MB
        toast.error("Image size should be less than 2MB");
        return;
      }

      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setOwnerPhoto(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCancel = () => {
    // Navigate back to retailer shop details without saving changes
    navigate("/retailer-shop-details", {
      state: {
        retail: retail, // Pass back the original retail data
      },
    });
  };

  // Format website for display (show without protocol if it's https://)
  const formatWebsiteForDisplay = (url) => {
    if (!url) return "";

    // Remove https:// if present
    if (url.startsWith("https://")) {
      return url.replace("https://", "");
    }
    // Remove http:// if present
    if (url.startsWith("http://")) {
      return url.replace("http://", "");
    }

    return url;
  };

  // Handle website input change
  const handleWebsiteChange = (value) => {
    setWebsite(value);
    if (websiteError) setWebsiteError("");
  };

  return (
    <div className="member-details-layout">
      <SideBar />

      <div className="member-details-main">
        {/* Header */}
        <div className="edit-header">
          <h2>Edit Profile</h2>
          <p>You can edit your details to update your profile</p>
        </div>

        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/dashboard" className="breadcrumb-link">
            Home
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <Link to="/manage-retailer" className="breadcrumb-link">
            Manage Retail Business
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <span className="breadcrumb-current">Edit Profile</span>
        </div>

        <div className="multi-form-container">
          <div className="progress-bar">
            {steps.map((item, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <div key={i} className="progress-step">
                  <button
                    type="button"
                    className={`circle ${
                      isCompleted ? "completed" : isActive ? "active" : ""
                    } clickable`}
                    onClick={() => handleStepClick(stepNum)}
                    disabled={isActive}
                    title={`Go to Step ${stepNum}: ${item.label}`}
                  >
                    {isCompleted ? "✓" : stepNum}
                  </button>
                  <div className="step-text">
                    <p
                      className={`step-label ${isActive ? "active-label" : ""}`}
                    >
                      {item.label}
                    </p>
                    <p
                      className={`step-subtext ${isActive ? "active-sub" : ""}`}
                    >
                      {item.sub}
                    </p>
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className={`line ${
                        isCompleted ? "line-filled" : "line-empty"
                      }`}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Step content */}
          <div className="step-content">
            {/* Step 1: Owner Details - Only First Name and Last Name */}
            {step === 1 && (
              <div>
                <h3>Owner Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      First Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        if (firstNameError) setFirstNameError("");
                      }}
                      placeholder="Enter first name"
                      disabled={loading || validatingFields}
                    />
                    <div className="error-container">
                      {firstNameError && (
                        <p className="error-text">{firstNameError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Last Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        if (lastNameError) setLastNameError("");
                      }}
                      placeholder="Enter last name"
                      disabled={loading || validatingFields}
                    />
                    <div className="error-container">
                      {lastNameError && (
                        <p className="error-text">{lastNameError}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Business Details - All business information */}
            {step === 2 && (
              <div>
                <h3>Business Details</h3>
                <hr />
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Business Logo</label>
                    <div className="photo-upload-container">
                      {ownerPhoto && (
                        <img
                          src={ownerPhoto}
                          alt="Business Logo"
                          className="owner-photo-preview"
                        />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="photo-upload-input"
                        disabled={
                          loading || validatingFields || loadingRetailTypes
                        }
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Business Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError("");
                      }}
                      placeholder="Enter business email"
                      disabled
                    />
                    <div className="error-container">
                      {emailError && <p className="error-text">{emailError}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Business Mobile <span className="required">*</span>
                    </label>
                    <div className="phone-input-container">
                      <PhoneInput
                        country={"gb"}
                        enableLongNumbers
                        value={mobile}
                        onChange={(value, country) => {
                          lastSelectedMobileCountryRef.current = country || null;
                          setMobile(clampAdminPhoneString(value));

                          if (mobileError) setMobileError("");
                        }}
                        inputProps={{
                          name: "mobile",
                          required: true,
                          disabled: loading || validatingFields,
                        }}
                        inputClass="phone-input"
                        containerClass="phone-container"
                      />
                    </div>
                    <div className="error-container">
                      {mobileError && (
                        <p className="error-text">{mobileError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Business Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={retailName}
                      onChange={(e) => {
                        setRetailName(e.target.value);
                        if (retailNameError) setRetailNameError("");
                      }}
                      placeholder="Enter business name"
                      disabled={loading || loadingRetailTypes}
                    />
                    <div className="error-container">
                      {retailNameError && (
                        <p className="error-text">{retailNameError}</p>
                      )}
                    </div>
                  </div>

                  {/* Retail Type Dropdown */}
                  <div className="form-group">
                    <label>
                      Retail Type <span className="required">*</span>
                    </label>
                    <Select
                      options={retailTypeOptions}
                      value={currentRetailTypeOption}
                      onChange={(option) => {
                        setRetailType(option.value);
                        if (retailTypeError) setRetailTypeError("");
                      }}
                      placeholder={
                        loadingRetailTypes
                          ? "Loading retail types..."
                          : "Select retail type"
                      }
                      isSearchable
                      classNamePrefix="custom-select"
                      isDisabled={loading || loadingRetailTypes}
                      isLoading={loadingRetailTypes}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: retailTypeError
                            ? "1.17px solid #dc3545"
                            : "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container">
                      {retailTypeError && (
                        <p className="error-text">{retailTypeError}</p>
                      )}
                    </div>
                  </div>

                  {/* Retail Sub Type Dropdown */}
                  <div className="form-group">
                    <label>
                      Retail Sub Type <span className="required">*</span>
                    </label>
                    <Select
                      options={retailSubTypeOptions}
                      value={currentRetailSubTypeOption}
                      onChange={(option) => {
                        setRetailSubType(option.value);
                        if (retailSubTypeError) setRetailSubTypeError("");
                      }}
                      placeholder={
                        retailType
                          ? "Select sub type"
                          : "First select retail type"
                      }
                      isSearchable
                      classNamePrefix="custom-select"
                      isDisabled={loading || !retailType || loadingRetailTypes}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: retailSubTypeError
                            ? "1.17px solid #dc3545"
                            : "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container">
                      {retailSubTypeError && (
                        <p className="error-text">{retailSubTypeError}</p>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Website</label>
                    <input
                      type="text"
                      value={formatWebsiteForDisplay(website)}
                      onChange={(e) => handleWebsiteChange(e.target.value)}
                      placeholder="Enter website (e.g., example.com, www.example.com)"
                      disabled={loading}
                    />
                    <div className="error-container">
                      {websiteError && (
                        <p className="error-text">{websiteError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Contract Signed</label>
                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={signedContract}
                        onChange={(e) => setSignedContract(e.target.checked)}
                        className="form-checkbox"
                        disabled={loading}
                      />
                      <span>Yes</span>
                    </div>
                    <div className="error-container" />
                  </div>
                  <div className="form-group">
                    <label>Business Status</label>
                    <Select
                      options={shopStatusOptions}
                      value={currentShopStatusOption}
                      onChange={(option) => setShopStatus(option.value)}
                      placeholder="Select business status"
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Business Address */}
            {step === 3 && (
              <div>
                <h3>Business Address</h3>
                <hr />
                <div className="form-grid">
                  {/* Row 1: Address line 1 and Address line 2 side by side */}
                  <div className="form-group">
                    <label>
                      Address line 1 <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={line1}
                      onChange={(e) => {
                        setLine1(e.target.value);
                        if (line1Error) setLine1Error("");
                      }}
                      placeholder="Enter address line 1"
                      disabled={loading}
                    />
                    <div className="error-container">
                      {line1Error && <p className="error-text">{line1Error}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Address line 2</label>
                    <input
                      type="text"
                      value={line2}
                      onChange={(e) => setLine2(e.target.value)}
                      placeholder="Enter address line 2"
                      disabled={loading}
                    />
                    <div className="error-container" />
                  </div>

                  {/* Row 2: Address line 3 - full width */}
                  <div className="form-group full-width">
                    <label>Address line 3</label>
                    <input
                      type="text"
                      value={line3}
                      onChange={(e) => setLine3(e.target.value)}
                      placeholder="Enter address line 3"
                      disabled={loading}
                    />
                    <div className="error-container" />
                  </div>

                  {/* Row 3: City and Region side by side */}
                  <div className="form-group">
                    <label>
                      City <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={selectedCity}
                      onChange={(e) => {
                        setSelectedCity(e.target.value);
                        if (cityError) setCityError("");
                      }}
                      placeholder="Enter city"
                      disabled={loading}
                    />
                    <div className="error-container">
                      {cityError && <p className="error-text">{cityError}</p>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Region <span className="required">*</span>
                    </label>
                    <Select
                      options={
                        selectedCountry
                          ? regionOptions[selectedCountry]?.map((r) => ({
                              value: r,
                              label: r,
                            })) || []
                          : []
                      }
                      value={currentRegionOption}
                      onChange={(option) => setSelectedCounty(option.value)}
                      placeholder="Select or type region"
                      isSearchable
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container" />
                  </div>

                  {/* Row 4: Post Code and Country side by side */}
                  <div className="form-group">
                    <label>
                      Post Code <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={(e) => {
                        setPostalCode(e.target.value);
                        if (postalCodeError) setPostalCodeError("");
                      }}
                      placeholder="Enter postal code"
                      disabled={loading}
                    />
                    <div className="error-container">
                      {postalCodeError && (
                        <p className="error-text">{postalCodeError}</p>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>
                      Country <span className="required">*</span>
                    </label>
                    <Select
                      options={Object.keys(regionOptions).map((c) => ({
                        value: c,
                        label: c,
                      }))}
                      value={currentCountryOption}
                      onChange={(option) => {
                        setSelectedCountry(option.value);
                        setSelectedCounty(""); // reset region
                        // Auto-select first region if available
                        const regions = regionOptions[option.value];
                        if (regions && regions.length > 0) {
                          setSelectedCounty(regions[0]);
                        }
                      }}
                      placeholder="Select or type country"
                      isSearchable
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused ? "none" : "none",
                          "&:hover": "none",
                          fontSize: "14px",
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <div className="error-container" />
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Status */}
            {step === 4 && (
              <div>
                <h3>Status</h3>
                <hr />
                <div className="form-grid center-step">
                  <div className="form-group">
                    <label>Status</label>
                    <Select
                      options={statusOptions}
                      value={status}
                      onChange={setStatus}
                      placeholder="Select Status"
                      classNamePrefix="custom-select"
                      isDisabled={loading}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          border: "1.17px solid rgba(209, 209, 209, 1)",
                          borderRadius: "17.6px",
                          background: "rgba(253, 253, 253, 1)",
                          minHeight: "38px",
                          boxShadow: state.isFocused
                            ? "0 0 0px rgba(21, 21, 22, 0.4)"
                            : "none",
                          "&:hover": "none",
                          fontSize: "15px",
                          color: "black",
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0 8px",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          color: "black",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "rgba(105, 105, 105, 1)",
                        }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          marginTop: "2px",
                          zIndex: 9999,
                        }),
                        option: (base, { isSelected, isFocused }) => ({
                          ...base,
                          backgroundColor: isSelected
                            ? "#e6f0ff"
                            : isFocused
                              ? "#e6f0ff"
                              : "#fff",
                          color: isSelected ? "#000" : "#000",
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      className="note-textarea"
                      rows="8"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Enter note here"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="btn-row">
            {step === 4 && (
              <button
                className="btn prev"
                onClick={handleCancel}
                disabled={loading || validatingFields}
              >
                Cancel
              </button>
            )}

            {step > 1 && (
              <button
                className="btn prev"
                onClick={prevStep}
                disabled={loading || validatingFields || loadingRetailTypes}
              >
                Previous
              </button>
            )}
            {step < 4 && (
              <button
                className="btn next"
                onClick={nextStep}
                disabled={loading || validatingFields || loadingRetailTypes}
              >
                {validatingFields ? "Checking..." : "Next"}
              </button>
            )}
            {step === 4 && (
              <button
                className="btn next"
                onClick={handleUpdate}
                disabled={loading || validatingFields || loadingRetailTypes}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditRetailerProfile;
